import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { usePrivy, useLogin, useWallets } from '@privy-io/react-auth';
import { useAccount, useDisconnect } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { ZNSAddressDisplay } from '../shared/ZNSAddressDisplay';

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: #000000;
  border-bottom: 1px solid #333333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 999;
  
  /* Desktop PWA constraint */
  @media (min-width: 768px) {
    max-width: 414px;
    margin: 0 auto;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
`;

const WalletButton = styled.button<{ $isConnected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  background: ${props => props.$isConnected 
    ? 'rgba(16, 185, 129, 0.15)' 
    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'};
  color: ${props => props.$isConnected ? '#10b981' : '#ffffff'};
  border: ${props => props.$isConnected 
    ? '1px solid rgba(16, 185, 129, 0.3)' 
    : 'none'};
  cursor: pointer;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.$isConnected 
      ? 'rgba(16, 185, 129, 0.2)' 
      : 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'};
    border-color: rgba(16, 185, 129, 0.5);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const WalletIcon = styled.i`
  font-size: 16px;
`;

const AddressText = styled.span`
  font-size: 12px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 480px) {
    max-width: 80px;
    font-size: 11px;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #000000;
  border: 1px solid #333333;
  border-radius: 12px;
  min-width: 200px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  overflow: hidden;
  
  @media (max-width: 480px) {
    min-width: 180px;
    right: -8px;
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }
  
  &:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
  
  i {
    font-size: 16px;
    width: 20px;
    text-align: center;
    color: #71717a;
  }
  
  &.disconnect {
    color: #ef4444;
    
    i {
      color: #ef4444;
    }
    
    &:hover {
      background: rgba(239, 68, 68, 0.1);
    }
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: #333333;
  margin: 4px 0;
`;

const WalletInfo = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #333333;
`;

const WalletLabel = styled.div`
  font-size: 10px;
  color: #71717a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const WalletAddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  color: #ffffff;
`;

const PWAHeader: React.FC = () => {
  const { ready, authenticated, user, logout } = usePrivy();
  const { wallets } = useWallets();
  const { address: wagmiAddress, isConnected: wagmiConnected } = useAccount();
  const { disconnect: wagmiDisconnect } = useDisconnect();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { login } = useLogin({
    onComplete: () => {
      console.log('✅ PWA: Privy login complete');
    },
    onError: (error) => {
      console.error('❌ PWA: Privy login error:', error);
    },
  });

  // Get the best available address
  const allWallets = wallets as any[];
  const embeddedEvmWallet = allWallets.find(w => 
    w.walletClientType === 'privy' && w.type === 'ethereum'
  );
  const connectedEvmWallet = allWallets.find(w => 
    w.walletClientType !== 'privy' && w.type === 'ethereum'
  );
  
  // Priority: embedded wallet > connected privy wallet > wagmi address
  const primaryAddress = embeddedEvmWallet?.address || connectedEvmWallet?.address || wagmiAddress;
  const isConnected = authenticated || wagmiConnected;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleWalletClick = () => {
    if (isConnected) {
      setDropdownOpen(!dropdownOpen);
    } else if (ready) {
      login();
    }
  };

  const handleViewProfile = () => {
    setDropdownOpen(false);
    navigate('/pwa/profile');
  };

  const handleSignOut = () => {
    setDropdownOpen(false);
    logout();
    if (wagmiConnected) {
      wagmiDisconnect();
    }
  };

  // Format address for display
  const formatAddress = (addr: string) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;

  // Get user display name
  const getDisplayName = () => {
    if (user?.email?.address) return user.email.address.split('@')[0];
    if (user?.google?.email) return user.google.email.split('@')[0];
    if (user?.twitter?.username) return `@${user.twitter.username}`;
    if (primaryAddress) return formatAddress(primaryAddress);
    return 'Connected';
  };

  return (
    <HeaderContainer>
      <LogoSection onClick={() => navigate('/pwa')}>
        <Logo src="/landing-assets/mavrk_word_logo.png" alt="Mavrk" />
      </LogoSection>
      
      <DropdownContainer ref={dropdownRef}>
        <WalletButton 
          $isConnected={isConnected}
          onClick={handleWalletClick}
          title={isConnected ? 'Wallet Options' : 'Sign In'}
        >
          <WalletIcon className={isConnected ? "fas fa-check-circle" : "fas fa-wallet"} />
          {isConnected ? (
            <AddressText>
              {primaryAddress ? (
                <ZNSAddressDisplay 
                  address={primaryAddress} 
                  truncateLength={4} 
                  showIcon={true}
                  iconSize={12}
                />
              ) : (
                getDisplayName()
              )}
            </AddressText>
          ) : (
            <span>{ready ? 'Sign In' : '...'}</span>
          )}
        </WalletButton>
        
        {isConnected && (
          <DropdownMenu $isOpen={dropdownOpen}>
            {/* Show wallet info */}
            {primaryAddress && (
              <WalletInfo>
                <WalletLabel>
                  {embeddedEvmWallet ? 'Smart Wallet' : 'Connected Wallet'}
                </WalletLabel>
                <WalletAddressRow>
                  {formatAddress(primaryAddress)}
                </WalletAddressRow>
              </WalletInfo>
            )}
            
            <DropdownItem onClick={handleViewProfile}>
              <i className="fas fa-user" />
              <span>View Profile</span>
            </DropdownItem>
            
            <DropdownItem onClick={() => { setDropdownOpen(false); navigate('/pwa/locks'); }}>
              <i className="fas fa-lock" />
              <span>Locks & TVL</span>
            </DropdownItem>
            
            <DropdownDivider />
            
            <DropdownItem onClick={handleSignOut} className="disconnect">
              <i className="fas fa-sign-out-alt" />
              <span>Sign Out</span>
            </DropdownItem>
          </DropdownMenu>
        )}
      </DropdownContainer>
    </HeaderContainer>
  );
};

export default PWAHeader;
